import { ApiError, apiClient } from "@/lib/apiClient";

import {
  parseSpaceVisibility,
  type GenerateRequest,
  type GenerateResponse,
  type ObjectId,
  type TextureAsset,
  type TextureId,
  type AssetUrlResponse,
  type SpaceDescriptor,
  type SpaceSummary,
  type SpaceVisibility,
  type SoundtrackAsset,
  type ObjectAssetMetadata,
  type PaginatedResponse,
} from "@mind-sands/shared";

// PaginatedResponse is now imported from @mind-sands/shared

// ── Asset URL Cache ──────────────────────────────────────────
// Resolves asset IDs to presigned R2 URLs via the backend,
// caching results to avoid redundant round-trips.

interface CachedUrl {
  url: string;
  expiresAt: number;
}

const assetUrlCache = new Map<string, CachedUrl>();
const inFlight = new Map<string, Promise<string>>();

/** Refresh 60 s before actual expiry */
const REFRESH_BUFFER_MS = 60_000;

async function fetchResolvedUrl(endpoint: string): Promise<string> {
  const cached = assetUrlCache.get(endpoint);
  if (cached && cached.expiresAt > Date.now() + REFRESH_BUFFER_MS) {
    return cached.url;
  }

  // De-duplicate concurrent requests for the same key
  const pending = inFlight.get(endpoint);
  if (pending) return pending;

  const promise = (async () => {
    const separator = endpoint.includes("?") ? "&" : "?";
    const { url, expiresIn } = await apiClient.get<AssetUrlResponse>(
      `${endpoint}${separator}resolve=true`,
    );
    assetUrlCache.set(endpoint, {
      url,
      expiresAt: Date.now() + expiresIn * 1000,
    });
    inFlight.delete(endpoint);
    return url;
  })();

  inFlight.set(endpoint, promise);
  return promise;
}

/** LOD tier info */
export interface LODTier {
  file: string;
  fileSize: number;
  triangles: number;
  vertices: number;
}

/** LOD variants */
export type AssetLODs =
  | { all: LODTier; thumb?: LODTier }
  | { high: LODTier; med?: LODTier; low?: LODTier; thumb?: LODTier };

/** Object asset metadata (mirrors server-side ObjectAsset) */
export interface ObjectAssetInfo {
  id: string;
  name: string;
  description: string;
  tags: string[];
  contentHash: string;
  addedAt: string;
  updatedAt: string;
  source: {
    boundingBox: [number, number, number];
    vertexCount: number;
    triangleCount: number;
    hasAnimations: boolean;
    fileSize: number;
  };
  lods: AssetLODs;
  /** Extensible per-asset metadata (archetype, effect params, etc.) */
  metadata?: ObjectAssetMetadata;
}

/** Sandbox API client — scene generation, projects, models, textures, spaces */
export const api = {
  /** List spaces (summary projection — lightweight, no rendering data) */
  async listSpaces(filters?: {
    category?: string;
    mood?: string;
  }): Promise<SpaceSummary[]> {
    return apiClient.get("/api/spaces", {
      query: { category: filters?.category, mood: filters?.mood },
    });
  },

  /** Get full space descriptor (for entering a space) */
  async getSpace(
    slug: string,
  ): Promise<{ space: SpaceDescriptor; visibility: SpaceVisibility }> {
    const body = await apiClient.get<
      SpaceDescriptor & { visibility?: SpaceVisibility }
    >(`/api/spaces/${slug}`);
    const { visibility, ...space } = body;
    return {
      space,
      visibility: parseSpaceVisibility(visibility),
    };
  },

  /** Compile a space into a scene descriptor (deterministic, no LLM) */
  async compileSpace(slug: string): Promise<{
    sceneDescriptor: import("@mind-sands/shared").SceneDescriptor;
  }> {
    return apiClient.post(`/api/spaces/${slug}/compile`);
  },

  /** Compile a SpaceDescriptor body into a SceneDescriptor (for real-time preview, no persistence) */
  async compileDescriptor(spaceDescriptor: SpaceDescriptor): Promise<{
    sceneDescriptor: import("@mind-sands/shared").SceneDescriptor;
  }> {
    return apiClient.post("/api/spaces/compile", spaceDescriptor);
  },

  /** Resolve the scene for a space — compiles SpaceDescriptor into SceneDescriptor */
  async getSpaceScene(slug: string): Promise<{
    sceneDescriptor: import("@mind-sands/shared").SceneDescriptor;
  }> {
    return apiClient.get(`/api/spaces/${slug}/scene`);
  },

  /** Generate a scene from natural language */
  async generate(request: GenerateRequest): Promise<GenerateResponse> {
    return apiClient.post("/api/generate", request);
  },

  /** Upload a space cover thumbnail (PNG blob) */
  async uploadScreenshot(
    spaceId: string,
    blob: Blob,
  ): Promise<{ thumbnailUrl: string }> {
    const formData = new FormData();
    formData.append("screenshot", blob, "cover.png");
    return apiClient.post(`/api/spaces/id/${spaceId}/screenshot`, formData);
  },

  /** Publish or hide a space on the landing gallery (admin / owner) */
  async updateSpaceVisibility(
    spaceId: string,
    visibility: SpaceVisibility,
  ): Promise<{ visibility: SpaceVisibility }> {
    return apiClient.patch(`/api/spaces/id/${spaceId}/visibility`, {
      visibility,
    });
  },

  /** Save space edits to the database (admin for curated, owner for user spaces) */
  async saveSpace(space: SpaceDescriptor): Promise<SpaceDescriptor> {
    try {
      return await apiClient.patch(`/api/spaces/id/${space.id}`, space);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        throw new Error("Sign in to save changes", { cause: err });
      }
      if (err instanceof ApiError && err.status === 403) {
        throw new Error("You don't have permission to save this space", {
          cause: err,
        });
      }
      throw err;
    }
  },

  /** Create a curated system space (admin only) */
  async createCuratedSpace(input: {
    slug: string;
    title: string;
    spaceData?: Partial<SpaceDescriptor>;
    originId?: string;
  }): Promise<SpaceDescriptor> {
    return apiClient.post("/api/spaces", { kind: "curated", ...input });
  },

  /** List spaces owned by the current user */
  async listMySpaces(): Promise<SpaceDescriptor[]> {
    return apiClient.get("/api/spaces/mine");
  },

  /** Create a guest session */
  async createGuestSession(): Promise<{ token: string; guestId: string }> {
    return apiClient.post("/api/auth/guest");
  },

  /** Delete all user data (GDPR) */
  async deleteUserData(token: string): Promise<void> {
    await apiClient.delete("/api/user/data", {
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  /** Export all user data (GDPR) */
  async exportUserData(token: string): Promise<unknown> {
    return apiClient.get("/api/user/export", {
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  /** List available 3D models with pagination */
  async listModels(options?: {
    page?: number;
    pageSize?: number;
    tag?: string;
  }): Promise<PaginatedResponse<ObjectAssetInfo>> {
    return apiClient.get("/api/models", {
      query: {
        page: options?.page,
        pageSize: options?.pageSize,
        tag: options?.tag,
      },
    });
  },

  /** Resolve a model to a direct R2 presigned URL (cached, avoids 302) */
  resolveModelUrl(id: ObjectId, lod?: string): Promise<string> {
    const endpoint = lod ? `/api/models/${id}?lod=${lod}` : `/api/models/${id}`;
    return fetchResolvedUrl(endpoint);
  },

  /** List available terrain textures with pagination */
  async listTextures(options?: {
    page?: number;
    pageSize?: number;
    tag?: string;
  }): Promise<PaginatedResponse<TextureAsset>> {
    return apiClient.get("/api/textures", {
      query: {
        page: options?.page,
        pageSize: options?.pageSize,
        tag: options?.tag,
      },
    });
  },

  /** Resolve a texture to a direct R2 presigned URL (cached, avoids 302) */
  resolveTextureUrl(id: TextureId): Promise<string> {
    return fetchResolvedUrl(`/api/textures/${id}`);
  },

  /** List all available soundtrack assets */
  async listSoundtracks(): Promise<SoundtrackAsset[]> {
    return apiClient.get("/api/soundtracks");
  },
};
